<template>
  <teleport to="body">
    <div
      v-if="!authStore.user?.has_updated_profile && isVisible && !props.isMenuOpen"
      class="profile-update-bubble"
      :style="{ top: `${positionTop}px`, left: `${positionLeft}px` }"
      ref="bubbleRef"
      @click="goToProfile"
    >
      <div class="bubble-content">
        <div class="bubble-text">
          <div class="bubble-title">Update Your Profile</div>
          <div class="bubble-description">
            Complete your profile details to get personalized insights and accurate substance tracking recommendations.
          </div>
        </div>
        <button class="bubble-close" @click="dismissBubble" aria-label="Dismiss">
          ×
        </button>
      </div>
      <div class="bubble-arrow" :style="{ left: `${arrowLeftPx}px` }"></div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { useAuthStore } from '../stores/auth'
import { onMounted, onBeforeUnmount, ref, withDefaults, defineProps, nextTick } from 'vue'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()

const props = withDefaults(defineProps<{ targetSelector?: string; offsetY?: number; isMenuOpen?: boolean }>(), {
  targetSelector: '.dropdown-toggle',
  offsetY: 8,
  isMenuOpen: false,
})

const bubbleRef = ref<HTMLElement | null>(null)
const positionTop = ref(90)
const positionLeft = ref(0)
const arrowLeftPx = ref(0)
const isVisible = ref(false)
let targetEl: HTMLElement | null = null
let roTarget: ResizeObserver | null = null
let roBubble: ResizeObserver | null = null

function updatePosition() {
  if (!targetEl) {
    targetEl = document.querySelector(props.targetSelector) as HTMLElement | null
  }
  if (!targetEl) return
  const targetRect = targetEl.getBoundingClientRect()
  const centerX = targetRect.left + targetRect.width / 2
  positionTop.value = Math.round(targetRect.bottom + (props.offsetY || 0))

  const bubbleWidth = bubbleRef.value?.offsetWidth ?? 320
  const margin = 8
  let bubbleX = Math.round(centerX - bubbleWidth / 2)
  bubbleX = Math.max(margin, Math.min(window.innerWidth - bubbleWidth - margin, bubbleX))
  positionLeft.value = bubbleX

  const ARROW_HALF = 6
  const padding = 12
  let apex = centerX - bubbleX
  apex = Math.max(padding, Math.min(bubbleWidth - padding, apex))
  arrowLeftPx.value = Math.round(apex - ARROW_HALF)
}

const goToProfile = async () => {
  isVisible.value = false
  if (router.currentRoute.value.path !== '/profile') {
    await router.push('/profile')
  }
}

const dismissBubble = () => {
  isVisible.value = false
}

function handleEvents() {
  updatePosition()
}

onMounted(async () => {
  targetEl = document.querySelector(props.targetSelector) as HTMLElement | null
  // Delay showing bubble to let header/layout finish animating
  const SHOW_DELAY_MS = 600
  setTimeout(async () => {
    isVisible.value = true
    updatePosition()
    await nextTick()
    updatePosition()
    requestAnimationFrame(() => updatePosition())
    // Stabilize for a short period in case fonts/layout shift
    const stabilizeMs = 500
    const interval = setInterval(() => updatePosition(), 60)
    setTimeout(() => clearInterval(interval), stabilizeMs)
  }, SHOW_DELAY_MS)

  window.addEventListener('resize', handleEvents)
  window.addEventListener('scroll', handleEvents, { passive: true })
  window.addEventListener('load', handleEvents)

  if ('ResizeObserver' in window) {
    if (targetEl) {
      roTarget = new ResizeObserver(() => updatePosition())
      roTarget.observe(targetEl)
    }
    if (bubbleRef.value) {
      roBubble = new ResizeObserver(() => updatePosition())
      roBubble.observe(bubbleRef.value)
    }
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleEvents)
  window.removeEventListener('scroll', handleEvents)
  window.removeEventListener('load', handleEvents)
  roTarget?.disconnect()
  roBubble?.disconnect()
})
</script>

<style scoped>
.profile-update-bubble {
  position: fixed;
  z-index: 2000;
  animation: bubbleSlideIn 0.25s ease-out;
  width: max-content;
  max-width: 90vw;
  cursor: pointer;
}

.bubble-content {
  pointer-events: none; /* allow wrapper to handle click */
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.18) 0%, rgba(118, 75, 162, 0.18) 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(20px) saturate(115%);
  -webkit-backdrop-filter: blur(20px) saturate(115%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  min-width: 280px;
  max-width: 360px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
}

.bubble-text { flex: 1; }

.bubble-title { font-weight: 600; font-size: 14px; margin-bottom: 4px; line-height: 1.2; }
.bubble-description { font-size: 12px; line-height: 1.4; opacity: 0.9; }

.bubble-close {
  pointer-events: auto; /* keep close button clickable */
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.bubble-close:hover { background: rgba(255, 255, 255, 0.2); transform: scale(1.1); }

.bubble-arrow {
  position: absolute;
  top: -6px;
  width: 0; height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid rgba(102, 126, 234, 0.25);
}

@keyframes bubbleSlideIn {
  from { opacity: 0; transform: translateY(-10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 768px) {
  .bubble-content { padding: 10px 14px; max-width: 300px; }
  .bubble-title { font-size: 13px; }
  .bubble-description { font-size: 11px; }
}

@media (max-width: 480px) {
  .bubble-content { min-width: 240px; max-width: 250px; }
}
</style>
